import { Component, Input, SimpleChanges } from '@angular/core';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

interface Collection {
  totalSize?: number;
  size?: number;
  storageSize?: number;
  freeStorageSize?: number;
  totalIndexSize?: number;
  count?: number;
  showDetails?: Boolean;
}

interface Summary {
  [key: string]: Collection;
}

@Component({
  selector: 'app-app-info',
  templateUrl: './app-info.component.html',
  styleUrls: ['./app-info.component.scss'],
})
export class AppInfoComponent {
  @Input() appInfoList: { [appName: string]: { [table: string]: any } } | null =
    null;

  @Input() appStorageInfoList: {
    [appName: string]: { [table: string]: any };
  } | null = null;

  objectKeys = Object.keys;
  summary: Summary = {};

  chartData: Summary = {};

  summaryChartSizeInstance: any;
  summaryChartRecordInstance: any;

  private themeObserver: MutationObserver | null = null;

  // ===============================
  // THEME HANDLING (CENTRALIZED)
  // ===============================
  getTheme() {
    const isDark = document.documentElement.classList.contains('dark-theme');

    return {
      text: isDark ? '#e4e6eb' : '#333',
      border: isDark ? '#1e1e1e' : '#ffffff',
      tooltipBg: isDark ? '#2a2a2a' : '#ffffff',
      gridLines: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    };
  }

  formatStorageSize(bytes: any) {
    const bytesInMB = 1024 * 1024; // 1 MB in bytes
    const bytesInGB = bytesInMB * 1024; // 1 GB in bytes

    const sizeInMB = bytes / bytesInMB;
    const sizeInGB = bytes / bytesInGB;

    return sizeInMB.toFixed(2) + ' MB';
  }

  getByteToMB(bytes: any) {
    const bytesInMB = 1024 * 1024; // 1 MB in bytes
    const sizeInMB = bytes / bytesInMB;
    return Number(sizeInMB.toFixed(2));
  }

  ngOnInit(): void {
    this.summary = this.calculateSummary(this.appInfoList || {});
    this.setupThemeObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appInfoList'] && this.appInfoList) {
      this.summary = this.calculateSummary(this.appInfoList);
      this.createChart(this.chartData);
    }

    if (changes['appStorageInfoList'] && this.appInfoList) {
      this.summary = this.calculateSummary(this.appInfoList);
      this.createChart(this.chartData);
    }
  }

  ngAfterViewInit() {
    if (Object.keys(this.chartData).length > 0) {
      this.createChart(this.chartData);
    }
  }

  // Setup observer to detect theme changes
  setupThemeObserver() {
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          // Theme changed, recreate charts
          if (Object.keys(this.chartData).length > 0) {
            this.createChart(this.chartData);
          }
        }
      });
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // Function to calculate totals
  calculateSummary(data: { [appName: string]: any }): Summary {
    const summary: Summary = {};
    const grandTotals: Collection = {
      totalSize: 0,
      size: 0,
      storageSize: 0,
      freeStorageSize: 0,
      totalIndexSize: 0,
      count: 0,
    };

    Object.entries(data).forEach(([appName, collections]) => {
      const appTotals: Collection = {
        totalSize: 0,
        size: 0,
        storageSize: 0,
        freeStorageSize: 0,
        totalIndexSize: 0,
        count: 0,
      };

      Object.entries(collections).forEach(([tableName, tableInfo]) => {
        const info = tableInfo as any;
        appTotals.totalSize! += info.totalSize || 0;
        appTotals.size! += info.size || 0;
        appTotals.storageSize! += info.storageSize || 0;
        appTotals.freeStorageSize! += info.freeStorageSize || 0;
        appTotals.totalIndexSize! += info.totalIndexSize || 0;
        appTotals.count! += info.count || 0;
      });

      let fileinfo = this.appStorageInfoList?.[appName] as any;
      appTotals.storageSize! += fileinfo?.data?.totalSize || 0;
      appTotals.count! += fileinfo?.data?.count || 0;

      summary[appName] = appTotals;

      grandTotals.totalSize! += appTotals.totalSize!;
      grandTotals.size! += appTotals.size!;
      grandTotals.storageSize! += appTotals.storageSize!;
      grandTotals.freeStorageSize! += appTotals.freeStorageSize!;
      grandTotals.totalIndexSize! += appTotals.totalIndexSize!;
      grandTotals.count! += appTotals.count!;
    });

    summary['Total'] = grandTotals;

    const { Total, ...rest } = summary;

    this.chartData = rest;

    return summary;
  }

  genChart(
    labels: string[],
    values: number[],
    element: string,
    title: string = 'Chart',
    subtitle: string = '',
  ) {
    const canvas = document.getElementById(element) as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to acquire context for the chart.');
      return;
    }

    // Get current theme colors
    const theme = this.getTheme();

    // --- GRADIENT DEFINITIONS START ---
    const gradientStroke1 = ctx.createLinearGradient(0, 0, 0, 300);
    gradientStroke1.addColorStop(0, '#fc4a1a');
    gradientStroke1.addColorStop(1, '#f7b733');

    const gradientStroke2 = ctx.createLinearGradient(0, 0, 0, 300);
    gradientStroke2.addColorStop(0, '#4776e6');
    gradientStroke2.addColorStop(1, '#8e54e9');

    const gradientStroke3 = ctx.createLinearGradient(0, 0, 0, 300);
    gradientStroke3.addColorStop(0, '#ee0979');
    gradientStroke3.addColorStop(1, '#ff6a00');

    const gradientStroke4 = ctx.createLinearGradient(0, 0, 0, 300);
    gradientStroke4.addColorStop(0, '#42e695');
    gradientStroke4.addColorStop(1, '#3bb2b8');

    // Added a 5th & 6th to handle more data labels if needed
    const gradientStroke5 = ctx.createLinearGradient(0, 0, 0, 300);
    gradientStroke5.addColorStop(0, '#673ab7');
    gradientStroke5.addColorStop(1, '#9c27b0');

    const gradientPalette = [
      gradientStroke1,
      gradientStroke2,
      gradientStroke3,
      gradientStroke4,
      gradientStroke5,
    ];
    // --- GRADIENT DEFINITIONS END ---

    // Cleanup existing instance to prevent hover glitches
    if (element === 'summary-chart-size' && this.summaryChartSizeInstance) {
      this.summaryChartSizeInstance.destroy();
    } else if (
      element === 'summary-chart-record' &&
      this.summaryChartRecordInstance
    ) {
      this.summaryChartRecordInstance.destroy();
    }

    const newChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            label:
              element === 'summary-chart-size'
                ? 'Storage Size'
                : 'Record Count',
            data: values,
            // Use the gradient palette here
            backgroundColor: gradientPalette,
            hoverBackgroundColor: gradientPalette,
            borderColor: theme.border,
            borderWidth: 0, // Set to 0 for no gaps as requested
            hoverOffset: 15,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '58%', // Thin elegant doughnut
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: false, // Set to false for Rectangle icons
              boxWidth: 40, // Width of the rectangle
              boxHeight: 12, // Height of the rectangle
              padding: 20,
              font: { size: 13, family: 'Roboto, sans-serif' },
              color: theme.text,
            },
          },
          tooltip: {
            enabled: true,
            padding: 12,
            cornerRadius: 8,
            backgroundColor: theme.tooltipBg,
            titleColor: theme.text,
            bodyColor: theme.text,
            callbacks: {
              label: (tooltipItem) => {
                const unit = element === 'summary-chart-size' ? ' MB' : '';
                return ` ${tooltipItem.label}: ${tooltipItem.raw}${unit}`;
              },
            },
          },
          title: {
            display: true,
            text: title,
            font: {
              size: 18,
              weight: 'bold',
            },
            color: theme.text,
          },
          subtitle: {
            display: !!subtitle,
            text: subtitle,
            font: { size: 12, style: 'italic' },
            padding: { top: 0, bottom: 15 },
            color: theme.text,
          },
        },
      },
    });

    if (element === 'summary-chart-size') {
      this.summaryChartSizeInstance = newChart;
    } else if (element === 'summary-chart-record') {
      this.summaryChartRecordInstance = newChart;
    }
  }

  createChart(data: any) {
    const labels = Object.keys(data);
    const sizes = labels.map((key) =>
      Number(this.getByteToMB(data[key].storageSize)).toFixed(2),
    );
    const total = sizes.reduce(
      (sum: number, count) => sum + parseFloat(count),
      0,
    );

    this.genChart(
      labels,
      sizes.map(Number),
      'summary-chart-size',
      'Storage Allocation by Applications (in MB)',
      'Total Storage Size :' + total,
    );

    const records = labels.map((key) => Number(data[key].count));

    const total_records = records.reduce((sum: any, count) => sum + count, 0);

    this.genChart(
      labels,
      records,
      'summary-chart-record',
      'Record Count Breakdown by Applications',
      'Total Records :' + total_records,
    );
  }

  // Public method to manually refresh charts (optional, can be called from outside)
  refreshCharts(): void {
    if (Object.keys(this.chartData).length > 0) {
      this.createChart(this.chartData);
    }
  }

  ngOnDestroy(): void {
    // Clean up theme observer
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }

    if (this.summaryChartSizeInstance) {
      this.summaryChartSizeInstance.destroy();
    }
    if (this.summaryChartRecordInstance) {
      this.summaryChartRecordInstance.destroy();
    }
  }
}
